const authObject = {
    isAuth0Loaded: false,
    isAuth0Loading: false,
    lock: null,
    settings: null,
    Auth0Lock: null,
};

const authLoadSampleRate = 300;

/**
 * @private
 * onAuth0Error: reject the promise when authetication library is loaded.
 *
 * @param {Function} reject the promise.
 */
const onAuth0Error = (reject) => {
    authObject.isAuth0Loading = false;
    authObject.isAuth0Loaded = false;
    reject(new Error(`Unable to load Auth0 API.`));
};

/**
 * @private
 * onAuth0Loaded: resolve the promise when authentication library is loaded 
 * @param {Function} resolve the promise.
 */
const onAuth0Loaded = (resolve) => {
    authObject.isAuth0Loaded = true;
    resolve();
};

/**
 * @private
 * loadAuth0Module: import the auth0 lock module.
 *
 * @param {Function} resolve the promise
 * @param {Function} reject the promise
 */
const loadAuth0Module = async (resolve, reject) => {
  await import('../../styles/components/auth0.scss');
  import('auth0-lock').then(({ Auth0Lock }) => {
    authObject.Auth0Lock = Auth0Lock;
    onAuth0Loaded.call(this, resolve);
  })
  .catch(onAuth0Error.bind(this, reject));
};

/**
 * @private
 * isAuthLoaded: resolve the promise when auth0 is loaded. Retry when auth0 is loading.
 *
 * @param {Function} resolve the promise. 
 */
const isAuthLoaded = (resolve) => {
    if (authObject.isAuth0Loaded) {
      resolve();
    } else {
      setTimeout(isAuthLoaded.bind(null, resolve), authLoadSampleRate);
    }
};

/**
 * @private
 * waitForAuth: wait for Authentication library, waiting a @see authLoadSampleRate milliseconds.
 * 
 * @returns {Promise} notify that authentication library is loaded.
 */
const waitForAuth = () =>
  new Promise((resolve) => {
    setTimeout(isAuthLoaded.bind(null, resolve), authLoadSampleRate);
});

/**
 * @private
 * getSignUpFieldByName: get the sign up field by name in setting authetication object.
 * @param {String} name of the sign up field.
 *  
 * @returns {Object} with the sign up field information. 
 */
const getSignUpFieldByName = (name) => authObject.settings.options
.additionalSignUpFields.find(item => item.name === name) || {};

/**
 * @private
 * goToNextPageAfterLogIn: redirect to the next page after user is autheticated.
 * @param {String} config with the next page to go.
 * 
 */
const goToNextPageAfterLogIn = (nextPage) => {
    if (nextPage) {
      window.location = nextPage;
    } else {
      window.location.reload();
    }
};

/**
 * @private
 * onSignUpReady: method to execute when sign up form is visible to the user.
 * This method helps to:
 *  Avoid numbers in zip code input
 *  Disable zip code when user is outside of United States.
 */
const onSignUpReady = () => {
    const zip = document.querySelector('[name="PostalCode"]');
    zip.setAttribute('maxlength', 5);
    zip.onkeypress = function (evt) {
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) { return false; }
      return true;
    };
    const outsideUS = document.querySelector('[name="OutsideUS"]');
    outsideUS.onchange = function () {
      zip.disabled = outsideUS.checked;
    };
    const outSideUsItem = getSignUpFieldByName("OutsideUS");
    if (outSideUsItem.prefill === 'true') {
      zip.disabled = true;
    }

    const outsideUSContainer = outsideUS.closest('div');
    const fieldsContainer = outsideUSContainer.parentElement;
    const lastFormElement = fieldsContainer.lastElementChild;

    lastFormElement.after(authObject.settings.legalContentSection.content.cloneNode(true));
};

/**
 * @private
 * onAuthenticated: method to execute after user is autheticated.
 * It helps to request NHS servers to create the cookies and get the main information of the user.
 * 
 * @param {Object} authResult information with the user token. 
 */
const onAuthenticated = async (authResult) => {
    document.querySelectorAll('.auth0-lock-close-button').forEach((element) => {
        element.remove();
    });

    const accessToken = authResult.accessToken;
    const keepMeLogged = document.querySelector('[name="KeepMeLogged"]');
    let keepMeLoggedValue = false;
    if (keepMeLogged) {
      keepMeLoggedValue = keepMeLogged.checked;
    }
    const url = authObject.settings.basic.auth0Authenticated;
    const parameters = {
      accessToken,
      keepMeLogged: keepMeLoggedValue
    };

    const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(parameters),
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        if (data.Success) {
          if (authObject.settings.afterLogin) {
            authObject.settings.afterLogin();
          }

          const event = new CustomEvent("loginSuccess");
          document.body.dispatchEvent(event);

          goToNextPageAfterLogIn(authObject.settings.nextPage);
        }
      }
};

/**
 * @private
 * onSigninReady: method to execute when sign in form is visible to the user.
 * This method helps to:
 *   Create the keep me login checkbox from authentication modal.
 */
const onSigninReady = () => {
    const referenceElement = document.querySelector('.auth0-lock-alternative');
    const parentContainer = referenceElement.parentElement;
    parentContainer.insertBefore(
        authObject.settings.keepMeLoggedSection.content.cloneNode(true),
        referenceElement);
    referenceElement.after(authObject.settings.legalContentSection.content.cloneNode(true));
};

/**
 * @public
 * loadAuth0: load the auth0 library.
 * @param {Function} authLoaded to be executed when the promise is resolved.
 * @param {*} authError to be executed when the promise is reject.
 *
 * @returns {Promise} that will be resolved. 
 */
export function loadAuth0(authLoaded, authError) {
    if (!authObject.isAuth0Loading) {
      authObject.isAuth0Loading = true;
      return new Promise(loadAuth0Module).then(authLoaded).catch(authError);
    }

    if (authObject.isAuth0Loaded) {
      return Promise.resolve().then(authLoaded).catch(authError);
    }

    return waitForAuth().then(authLoaded).catch(authError);
};

/**
 * @public
 * createAuthInstance: create the authetication instance
 * @param {Object} settings of the authentication modal.
 * @param {Function} onCloseModal callback to be execute in hide event.
 */
export function createAuthInstance(settings, onCloseModal) {
    authObject.settings = settings;
    authObject.lock = new authObject.Auth0Lock(
      settings.basic.auth0ClientId,
      settings.basic.auth0Domain,
      settings.options);
    authObject.lock.on('signup ready', onSignUpReady);
    authObject.lock.on('authenticated', onAuthenticated);
    authObject.lock.on('signin ready', onSigninReady);
    authObject.lock.on('hide', onCloseModal);

}

/**
 * @public
 * showModal: open the authentication modal.
 *
 */
export function showModal() {
    authObject.lock.show();
}